import React, { useEffect, useMemo } from 'react';
import { Provider, useDispatch } from 'react-redux';
import useSelectorUser from '@/hooks/use-selector-user';
import { actions, AppStore } from '@/store';
import { EAuthModalPage } from '@/store/types';
import Modal from '../../common-modal';
import { AuthLayout } from './common/auth-layout';
import { InputEmail } from './input-email';
import { InputEmailSocial } from './input-email-social';
import { InputNewPassword } from './input-new-password';
import { InputPassword } from './input-password';
import { SendEmailAndInputConfirmCode } from './send-email-and-input-confirm-code';
import { Success } from './success';
import { FindPasswordInputNewPassword } from './find-password-input-new-password';
import { AgreePolicy } from './agree-policy';
import { WillBeDeletedAccount } from './will-be-deleted-account';
import { SignUp } from './signup';
import SelectUser from './select-user';
import { CreatorSignUp } from './creator-signup';
import { AuthEmail } from './auth-email';
import { Complete, CompletePasswordMobile } from './complete';
import { ResetPassword } from './reset-password';

interface AuthModalComponentProps {
  onSuccess?: () => void;
  onClose?: () => void;
  signup: boolean;
  reset?:boolean;
  gotoAuthRegister?: () =>void;
  gotoAuthLogin?: () => void;
  gotoAuthReset?: () => void;
}
const AuthModalComponent: React.FC<AuthModalComponentProps> = ({
  onSuccess, onClose, signup, reset, gotoAuthRegister, gotoAuthLogin, gotoAuthReset,
}) => {
  const { currentPage } = useSelectorUser('authModal');
  const dispatch = useDispatch();

  useEffect(() => {
    // 페이지에 따른 첫 컴포넌트
    if (signup) {
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SELECT_USER));
    } else if (reset) {
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.AUTH_EMAIL));
      dispatch(actions.user.authModal.setConfirmCodeType('pwreset'));
    } else {
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.INPUT_EMAIL));
    }
  }, [dispatch, reset, signup]);

  const RenderPage = useMemo(() => {
    switch (currentPage) {
      /** 기존 페이지 */
      case EAuthModalPage.INPUT_EMAIL_SOCIAL: return InputEmailSocial;
      case EAuthModalPage.INPUT_PASSWORD: return InputPassword;
      case EAuthModalPage.SUCCESS: return Success;
      case EAuthModalPage.SEND_EMAIL_AND_INPUT_CONFIRM_CODE: return SendEmailAndInputConfirmCode;
      case EAuthModalPage.INPUT_NEW_PASSWORD: return InputNewPassword;
      case EAuthModalPage.FIND_PASSWORD_INPUT_NEW_PASSWORD: return FindPasswordInputNewPassword;

      /** 수정한 페이지 */
      case EAuthModalPage.INPUT_EMAIL: return InputEmail;
      case EAuthModalPage.ARGREE_POLICY: return AgreePolicy;
      case EAuthModalPage.WILL_BE_DELETED_ACCOUNT: return WillBeDeletedAccount;

      /**  추가한 페이지 */
      case EAuthModalPage.SELECT_USER: return SelectUser;
      case EAuthModalPage.SIGN_UP: return SignUp;
      case EAuthModalPage.CREATOR_SIGN_UP: return CreatorSignUp;
      case EAuthModalPage.AUTH_EMAIL: return AuthEmail;
      case EAuthModalPage.COMPLETE: return Complete;
      case EAuthModalPage.RESET_PASSWORD: return ResetPassword;
      case EAuthModalPage.COMPLETE_PASSWORD_MOBILE: return CompletePasswordMobile;

      default: return () => null;
    }
  }, [currentPage]);

  useEffect(() => {
    dispatch(actions.user.authModal.setOnSuccess(onSuccess));
    dispatch(actions.user.authModal.setOnClose(onClose));
    dispatch(actions.user.authModal.setGotoAuthRegister(gotoAuthRegister));
    dispatch(actions.user.authModal.setGotoAuthLogin(gotoAuthLogin));
    dispatch(actions.user.authModal.setGotoAuthReset(gotoAuthReset));
  }, [dispatch, gotoAuthLogin, gotoAuthRegister, gotoAuthReset, onClose, onSuccess]);

  return (
    <Modal show closeOnClickOverlay={false} backgroundOpacity={1}>
      <AuthLayout>
        <RenderPage />
      </AuthLayout>
    </Modal>
  );
};

const AuthModal: React.FC<AuthModalComponentProps> = (props) => (
  <Provider store={AppStore}>
    <AuthModalComponent {...props} />
  </Provider>
);

export default AuthModal;
